import React, { useRef, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faPaperclip } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { Message } from '../../types';
import { MessageItem } from './MessageItem';
import './ChatMessages.css';
import { useAuthToken } from '../../services/auth';
import { Config } from '../../types';
import { uploadDocument } from '../../services/api';

interface Props {
  messages: Message[];
  isLoading: boolean;
  input: string;
  setInput: (input: string) => void;
  onSendMessage: () => void;
  onCitationClick: (citation: string) => void;
  onFollowupClick: (question: string) => void;
  config: Config;
  userId: string;
}

export const ChatMessages: React.FC<Props> = ({
  messages,
  isLoading,
  input,
  setInput,
  onSendMessage,
  onCitationClick,
  onFollowupClick,
  config,
  userId
}) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState<{ text: string; type: 'success' | 'error' | '' }>({ text: '', type: '' });
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const { getToken } = useAuthToken();

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add an additional effect to ensure scrolling works when messages load
  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);
    return () => clearTimeout(timer);
  }, [messages.length]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSendMessage();
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`; // Max height of 200px
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  // File upload functions
  const handleFileUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    await uploadFiles(Array.from(files));
    
    // Reset the input so the same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (!droppedFiles || droppedFiles.length === 0) return;
    
    await uploadFiles(Array.from(droppedFiles));
  };

  const uploadFiles = async (files: File[]) => {
    // Check if files are of allowed types (documents)
    const allowedTypes = [
      'application/pdf', 
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
      'application/msword', // doc
      'text/plain', // txt
      'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
      'application/vnd.ms-powerpoint', // ppt
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
      'application/vnd.ms-excel', // xls
    ];
    
    const validFiles = files.filter(file => allowedTypes.includes(file.type));
    
    if (validFiles.length === 0) {
      setUploadMessage({ text: t('chat.onlyDocumentsAllowed'), type: 'error' });
      setTimeout(() => {
        setUploadMessage({ text: '', type: '' });
      }, 5000);
      return;
    }
    
    try {
      setIsUploading(true);
      
      // Get authentication token
      const token = await getToken();
      if (!token) {
        setUploadMessage({ text: 'Authentication failed', type: 'error' });
        setIsUploading(false);
        return;
      }
      
      let successCount = 0;
      let failCount = 0;
      
      for (const file of validFiles) {
        try {
          // Directly use fetch with formData to match the implementation in settings
          const formData = new FormData();
          formData.append('file', file);
          formData.append('userId', userId);
          formData.append('isAdminContent', 'false'); // Always mark as personal document
          
          const response = await fetch(`${config.apiUrl}/api/documents`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });
          
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
            const errorData = await response.json();
            console.error(`Error uploading ${file.name}:`, errorData);
          }
        } catch (error) {
          failCount++;
          console.error('Error uploading file:', error);
        }
      }
      
      // Set appropriate message based on results
      if (successCount > 0 && failCount === 0) {
        if (successCount === 1) {
          setUploadMessage({ 
            text: t('chat.uploadSuccess', { filename: validFiles[0].name }), 
            type: 'success' 
          });
        } else {
          setUploadMessage({ 
            text: t('chat.uploadSuccessMultiple', { count: successCount }), 
            type: 'success' 
          });
        }
        setUploadSuccess(true);
      } else if (successCount > 0 && failCount > 0) {
        setUploadMessage({ 
          text: t('chat.uploadPartialSuccess', { successCount, failCount }), 
          type: 'error' 
        });
      } else {
        setUploadMessage({ 
          text: t('chat.uploadFailureMultiple', { count: failCount }), 
          type: 'error' 
        });
      }
      
      // Hide message after delay
      setTimeout(() => {
        setUploadMessage({ text: '', type: '' });
        setUploadSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error('Error uploading files:', error);
      setUploadMessage({ 
        text: t('chat.uploadError', { error: error instanceof Error ? error.message : 'Unknown error' }), 
        type: 'error' 
      });
      
      setTimeout(() => {
        setUploadMessage({ text: '', type: '' });
      }, 5000);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <div className="messages-container">
        <div className="messages-list">
          {messages.length === 0 ? (
            <div style={{ flex: 1 }} /> // Empty spacer when no messages
          ) : (
            messages.map((message, index) => (
              <MessageItem
                key={index}
                message={message}
                onCitationClick={onCitationClick}
                onFollowupClick={onFollowupClick}
              />
            ))
          )}
          {isLoading && (
            <div className="spinner">
              <div className="loading-spinner" />
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      
      <div className="input-container">
        {uploadMessage.text && (
          <div className={`upload-message ${uploadMessage.type}`}>
            <span>{uploadMessage.text}</span>
            {uploadSuccess && <p className="personal-note">{t('chat.personalDocument')}</p>}
          </div>
        )}
        <textarea
          ref={textareaRef}
          value={input}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={t('chat.writeMessage')}
          rows={1}
          className={`message-input ${isDragging ? 'dragging' : ''}`}
          disabled={isLoading || isUploading}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        />
        <button 
          className="action-button upload-button"
          onClick={handleFileUploadClick}
          disabled={isLoading || isUploading}
          title={t('chat.attachFile')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M4.5 3a2.5 2.5 0 0 1 5 0v9a1.5 1.5 0 0 1-3 0V5a.5.5 0 0 1 1 0v7a.5.5 0 0 0 1 0V3a1.5 1.5 0 1 0-3 0v9a2.5 2.5 0 0 0 5 0V5a.5.5 0 0 1 1 0v7a3.5 3.5 0 1 1-7 0V3z"/>
          </svg>
        </button>
        <button 
          className="action-button" 
          onClick={onSendMessage}
          disabled={isLoading || isUploading || !input.trim()}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
        
        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.txt,.ppt,.pptx,.xls,.xlsx"
          multiple
        />
      </div>
    </>
  );
}; 